import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import compact from 'lodash/compact';
import delay from 'lodash/delay';
import flatMap from 'lodash/flatMap';
import { useSelector } from 'react-redux';
import { getData, getNameBySlug, getCurrencyRates } from '@yme/api';
import { ArrowForward, SortFull, SortHalf } from '@yme/assets/icons';
import { Button, GHOST_VARIANT, LEADING_ICON, SIZE_44 } from '@yme/atoms/button';
import { Dropdown } from '@yme/atoms/dropdown';
import { ClearAll } from '@yme/atoms/filters-elements';
import { Col, Container, Row } from '@yme/atoms/grid';
//import { InfiniteScroll } from '@yme/atoms/infinite-scroll';
import { InfoBlockEmptyResults } from '@yme/atoms/info-block-empty-results';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { SORT_PRICE_ASC, SORT_PRICE_DESC } from '@yme/common/constants';
import { ExperienceFilters, FilterContext, } from '@yme/components/Destinations/ExperienceGrid/components/ExperienceFilters';
import { useFilter } from '@yme/components/Destinations/ExperienceGrid/components/ExperienceFilters/hooks';
import { getExperienceListingAnalyticsData, getFilterAnalyticsData, sortDestinations, } from '@yme/components/Destinations/ExperienceGrid/helpers';
import { IndentWrapper } from '@yme/components/Separator';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { DestinationCard } from '@yme/molecules/cards';
import { GridSkeleton } from '@yme/molecules/grid-skeleton';
import { getIsInAuthor, getRootLang, getRTL } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel2 } from '@yme/tokens/typography';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { getDataForDestinationCard } from '../UnitRelatedExperience/helpers';
import { CardsPerPage, FilterName, GET_DESTINATIONS_FOR_ROOT_ENDPOINT } from './constants';
import { ColStyled, SortOrderStyled } from './styled';
export const ExperienceGrid = ({ experiencesRootPath, edpPageUrl, productNamesHyperdriveEndpoint, isRemoveBottomIndent, labels, isInEditor, currencyRatesHyperdriveEndpoint, isCurrencyConverterEnabled, nationalCurrencyCode, }) => {
    const isFirstUpdate = useRef(true);
    const [destinations, setDestinations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [isFilterCleared, setIsFilterCleared] = useState(false);
    const [converterRate, setConverterRate] = useState();
    const language = useSelector(getRootLang);
    const isInAuthor = useSelector(getIsInAuthor);
    const isRightToLeft = useSelector(getRTL);
    const { t } = useTranslation();
    const { isMobileView, isInfiniteDesktopView } = useWindowSize();
    const cardsPerPage = isInfiniteDesktopView ? CardsPerPage.DESKTOP : CardsPerPage.MOBILE;
    const sortFilterOptions = [
        {
            value: SORT_PRICE_ASC,
            label: t('productsGrid_priceLowToHigh_option'),
        },
        {
            value: SORT_PRICE_DESC,
            label: t('productsGrid_priceHighToLow_option'),
        },
    ];
    const [sortOrderOption, setSortOrderOption] = useState(sortFilterOptions[0].value);
    const handleSortOrderChange = useCallback((value) => {
        setSortOrderOption(value);
    }, []);
    useEffect(() => {
        if (sortOrderOption) {
            setDestinations((prev) => {
                const prevData = Array.from(prev);
                return sortDestinations({
                    destinations: prevData,
                    sortOrderOption,
                });
            });
        }
    }, [sortOrderOption]);
    useEffect(() => {
        if (!experiencesRootPath || !edpPageUrl || !language)
            return;
        const getDestinationsForRoot = async () => {
            try {
                setIsLoading(true);
                const response = await getData(`${GET_DESTINATIONS_FOR_ROOT_ENDPOINT}${experiencesRootPath}`);
                const data = response.data?.destinationList?.items ?? [];
                const slugs = compact(flatMap(data, FilterName.UNITS));
                const unitNames = await getNameBySlug(productNamesHyperdriveEndpoint, slugs);
                const filteredData = data.filter((destinations) => destinations.expPrice !== null &&
                    destinations.expLocation !== null &&
                    destinations.expType !== null);
                const destinationData = filteredData.map((destination) => getDataForDestinationCard(destination, language, edpPageUrl, isInAuthor, unitNames, true));
                setDestinations(sortDestinations({
                    destinations: destinationData,
                    sortOrderOption,
                }));
            }
            catch (error) {
            }
            finally {
                setIsLoading(false);
                setIsLoaded(true);
            }
        };
        getDestinationsForRoot();
    }, [
        experiencesRootPath,
        edpPageUrl,
        language,
        isInAuthor,
        productNamesHyperdriveEndpoint,
        labels,
        cardsPerPage,
        sortOrderOption,
    ]);
    const filterContextData = useFilter(destinations);
    const { filteredData, appliedFilters, filterSearchParams, countTotalAppliedValues, config, onClearFilters, } = filterContextData;
    const slicedDestinations = useMemo(() => filteredData.slice(0, cardsPerPage * page), [filteredData, cardsPerPage, page]);
    const handleClearFilters = useCallback(() => {
        onClearFilters();
        delay(() => setIsFilterCleared(true), 300);
    }, [onClearFilters]);
    useEffect(() => {
        const hasConfigOptions = config.some((item) => item.options && item.options.length);
        if (!hasConfigOptions) {
            return;
        }
        if ((isFirstUpdate.current && filteredData.length) ||
            (isFirstUpdate.current && filterSearchParams.length)) {
            isFirstUpdate.current = false;
            const filterAnalyticsData = getFilterAnalyticsData(appliedFilters, config);
            const analyticsData = getExperienceListingAnalyticsData(filteredData);
            setDigitalData(DigitalDataMutation.ELP_VIEW, {
                category: 'experiences-listing',
                resultCount: filteredData.length,
                pagesCount: Math.ceil(filteredData.length / cardsPerPage),
                listItems: analyticsData,
                filterBy: filterAnalyticsData,
            });
        }
    }, [appliedFilters, cardsPerPage, config, filterSearchParams, filteredData]);
    const isShowLoadMoreBtn = slicedDestinations.length !== filteredData.length;
    const handleLoadMore = useCallback(() => {
        setPage((prev) => prev + 1);
    }, []);
    useEffect(() => {
        if (isCurrencyConverterEnabled) {
            getCurrencyRates(`${currencyRatesHyperdriveEndpoint}${nationalCurrencyCode}`).then((result) => {
                setConverterRate(result);
            });
        }
    }, [currencyRatesHyperdriveEndpoint, isCurrencyConverterEnabled, nationalCurrencyCode]);
    /* const handleReachEnd = useCallback(() => {
      if (isShowLoadMoreBtn) {
        handleLoadMore();
      }
    }, [handleLoadMore, isShowLoadMoreBtn]); */
    const sortOrderComponent = (React.createElement(SortOrderStyled, null,
        React.createElement(Dropdown, { id: "sort_order", title: t('filters_sortBy_label'), isInheritBackground: true, useForMobile: true, hasRadioButton: true, leftSideIconType: isInfiniteDesktopView ? SortHalf : SortFull, isRightToLeft: isRightToLeft, items: sortFilterOptions, activeItem: sortOrderOption, onChange: handleSortOrderChange })));
    return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent, isRightToLeft: isRightToLeft },
        React.createElement(FilterContext.Provider, { value: filterContextData },
            React.createElement(Container, { pt: "32px", lgPt: "48px" },
                React.createElement(Row, { mb: "24px" },
                    React.createElement(Col, null,
                        React.createElement(FlexWrapper, { justifyContent: "space-between" },
                            React.createElement(HeadingLevel2, null, t('destinations_experiencesGrid_header')),
                            React.createElement(FlexWrapper, { gap: "12px", alignItems: "center" },
                                Boolean(countTotalAppliedValues) && (React.createElement(ClearAll, { label: t('filters_clearFilters_label'), isRightToLeft: isRightToLeft, onClick: handleClearFilters })),
                                isInfiniteDesktopView && sortOrderComponent)))),
                React.createElement(ExperienceFilters, { cardsPerPage: cardsPerPage, labels: labels, isFilterCleared: isFilterCleared, sortOrderComponent: sortOrderComponent }),
                !isLoaded && !isInEditor ? (React.createElement(Row, null,
                    React.createElement(GridSkeleton, { currentPerPageValue: isInfiniteDesktopView ? 12 : 8 }))) : null,
                isLoaded && (React.createElement(Row, null, slicedDestinations.length ? (slicedDestinations.map((item) => (React.createElement(ColStyled, { key: item.edpURL + item.title, md: 4, lg: 4 },
                    React.createElement(DestinationCard, { cardProps: item, converterRate: converterRate, isRightToLeft: isRightToLeft }))))) : (React.createElement(InfoBlockEmptyResults, { titleEmptyResults: t('destinations_noExperiencesFound_label'), descriptionEmptyResults: t('destinations_couldNotFoundExperiences_hint'), isFullWidth: true })))),
                isShowLoadMoreBtn && (React.createElement(Row, { mb: "54px" },
                    React.createElement(Col, { justifyContent: "center", display: "flex" },
                        React.createElement(Button, { width: isMobileView ? '100%' : 'auto', buttonVariant: GHOST_VARIANT, hasIconButton: LEADING_ICON, size: SIZE_44, iconComponent: ArrowForward, iconRotate: 90, onClick: handleLoadMore, isRightToLeft: isRightToLeft, label: t('common_loadMore_label'), title: t('common_loadMore_label') }))))),
            React.createElement(SpinnerLoader, { isLoading: isLoading, fullWidth: true }))));
};
ExperienceGrid.displayName = 'ExperienceGrid_AEMComponent';
